import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import VaccinesIcon from '@mui/icons-material/Vaccines'
import LocalCarWashIcon from '@mui/icons-material/LocalCarWash'
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation'
import BuildIcon from '@mui/icons-material/Build'
import ReportIcon from '@mui/icons-material/Report'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import NoCrashIcon from '@mui/icons-material/NoCrash'
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled'
import { Link, useNavigate } from 'react-router-dom'
import API from '../../../services/API'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import ToDoList from './ToDoList'
import Quill from '../../../components/Quill'
import QuickActionCard from '../../../components/Cards/QuickActionCard'
import { GlobalContext, TenantSettingsContext, UserContext } from '../../../App'
import StatusCard from '../../../components/Cards/StatusCard'
import FolderSharedIcon from '@mui/icons-material/FolderShared'
import TeamActions from './TeamActions'
import ChecklistIcon from '@mui/icons-material/Checklist'
import { OpenInNewRounded } from '@mui/icons-material'
import CustomQuickLinks from './dashboardParts/CustomQuickLinks'
import ActiveShiftDashboard from './activeShift/ActiveShift'
import ActiveResourceCard from './ActiveResourceCard'
import GettingStartedCard from './GettingStartedCard'
import OnCallCard from './OnCallCard'
import CqcReadinessCard from './CqcReadinessCard'

const MainDashboard = () => {
  const { user } = useContext(UserContext)
  const { tenantInfo } = useContext(GlobalContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const userTeams = user.employee?.teams ?? []
  const isSubcontractor = !!user?.subcontractor && !user?.employee
  const isGovernance = userTeams.includes('Governance') || user?.isSiteAdmin
  const isFleet = userTeams.includes('Fleet') || user?.security?.isSiteAdmin
  const [dashboardData, setDashboardData] = useState({})
  const [fetchError, setFetchError] = useState(false)
  // Whether team actions exist decides the page layout (and where the CQC
  // readiness card lives) - don't commit to a layout until we know.
  const [dashboardLoaded, setDashboardLoaded] = useState(false)

  useEffect(() => {
    API.get('/dashboard-data')
      .then((res) => {
        if (res.status === 200) {
          setDashboardData(res.data)
        }
      })
      .catch((e) => {
        console.log(e)
        setFetchError(true)
      })
      .finally(() => setDashboardLoaded(true))
  }, [])

  const somethingToDo = Object.keys(dashboardData?.toDo ?? []).length > 0
  const teamActions = Object.keys(dashboardData?.teamActions ?? []).length > 0
  // PTS duties are open to every employee, so the shortcut only depends on the
  // tenant having the PTS module rather than a per-user permission.
  const showPTSDuty =
    !!user?.employee && Number(tenantInfo?.subscriptionTier) >= 3

  return (
    <Grid container spacing={2}>
      <GettingStartedCard />
      <OnCallCard />
      {somethingToDo && (
        <Grid item xs={12}>
          <ToDoList dashboardData={dashboardData.toDo} />
        </Grid>
      )}
      <Grid item container xs={12}>
        <ActiveShiftDashboard />
      </Grid>
      <ActiveResourceCard />

      <Grid item xs={12} lg={teamActions ? 4 : 12}>
        <Grid container spacing={0.5}>
          <Grid item xs={12}>
            <Typography variant="title3">Quick Links</Typography>
          </Grid>
          {showPTSDuty && (
            <QuickActionCard
              url="/pts"
              color="#2DD4BF"
              icon={DirectionsCarFilledIcon}
              label="My PTS Duty"
              largeSize={teamActions ? 12 : 6}
            />
          )}
          {isFleet && (
            <QuickActionCard
              url="make-ready-overview"
              color="#60A5FA"
              icon={ChecklistIcon}
              label="Make Ready"
              largeSize={teamActions ? 12 : 6}
            />
          )}
          <QuickActionCard
            url="/epcr"
            color="#34D399"
            icon={FolderSharedIcon}
            label="Patient Care Record"
            largeSize={teamActions ? 12 : 6}
          />
          <QuickActionCard
            url="/checkmedsbag"
            color="#22D3EE"
            icon={VaccinesIcon}
            label="Meds Management"
            largeSize={teamActions ? 12 : 6}
          />
          <QuickActionCard
            url="/report-defect"
            color="#FB923C"
            icon={BuildIcon}
            label="Report a Defect"
            largeSize={teamActions ? 12 : 6}
          />
          <QuickActionCard
            url="/report-incident"
            color="#F472B6"
            icon={ReportIcon}
            label="Report an Incident"
            largeSize={teamActions ? 12 : 6}
          />
          {orgSettings?.speakUpSettings?.enabled !== false && (
            <QuickActionCard
              url="/speak-up"
              color="#A78BFA"
              icon={RecordVoiceOverIcon}
              label="Speak Up"
              largeSize={teamActions ? 12 : 6}
            />
          )}
          <QuickActionCard
            url="/start-of-shift"
            color="#CBD5E1"
            icon={NoCrashIcon}
            label="Start of Shift Check"
            largeSize={teamActions ? 12 : 6}
          />
          {!isSubcontractor && (
            <QuickActionCard
              url="/vehicle-clean"
              color="#7DD3FC"
              icon={LocalCarWashIcon}
              label="Vehicle Clean"
              largeSize={teamActions ? 12 : 6}
            />
          )}
          {!isSubcontractor && (
            <QuickActionCard
              url="/record-fuel"
              color="#FDBA74"
              icon={LocalGasStationIcon}
              label="Record Fuel"
              largeSize={teamActions ? 12 : 6}
            />
          )}
          <CustomQuickLinks teamActions={teamActions} />
        </Grid>
      </Grid>
      {teamActions ? (
        <Grid item xs={12} lg={8}>
          <Stack spacing={2}>
            <TeamActions dashboardData={dashboardData?.teamActions} />
            <CqcReadinessCard embedded />
          </Stack>
        </Grid>
      ) : (
        // Rendering the standalone card before /dashboard-data resolves made
        // it flash on the left then jump into the right-hand column once
        // team actions arrived - wait until the layout is known.
        dashboardLoaded && <CqcReadinessCard />
      )}
    </Grid>
  )
}

export default MainDashboard
